<template>
  <div class="legal-texts-editor">
    <InformationModal v-if="showPreview" ref="informationModalRef" @modal-closed="onModalClosed"/>

    <!-- Language Selector -->
    <div class="language-selector-section mb-4">
      <div class="selector-content">
        <div class="language-selector-wrapper">
          <div class="language-icon-wrapper">
            <i class="uil-globe"></i>
          </div>
          <app-multiselect
            v-model="selectedLanguageObj"
            :options="languages"
            :is-searchable="false"
            :allow-empty="false"
            :hide-label="true"
            :close-on-select="true"
            label="name"
            track-by="lang"
            :placeholder="$t('general.select_language')"
            @input="onLanguageChange"
          />
        </div>
        <button class="btn btn-outline-primary preview-btn" type="button" @click="openPreview">
          <i class="uil-eye"></i>
          <span>{{ $t('general.preview') }}</span>
        </button>
      </div>
    </div>

    <div v-if="errorMessages.length > 0" class="uk-alert-danger mb-3" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <div v-for="(error, index) in errorMessages" :key="index">
        <p style="color:red;">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </div>

    <div v-if="successMessage" class="uk-alert-success mb-3" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <p style="color:green;">
        <i class="uil-check-circle"></i> {{ successMessage }}
      </p>
    </div>

    <default-loading v-if="loading"></default-loading>

    <div v-else>
      <!-- KVKK  -->
      <div class="editor-section mb-4">
        <div class="section-header-collapsible" @click="toggleKvkkSection">
          <div class="d-flex align-items-center">
            <i class="uil-shield-check mr-2"></i>
            <div>
              <h4 class="mb-0"> {{ $t('general.kvkk_info_text') }}</h4>
            </div>
          </div>
          <i :class="['collapse-icon', isKvkkOpen ? 'uil-angle-up' : 'uil-angle-down']"></i>
        </div>

        <div v-show="isKvkkOpen" class="section-content">
          <div class="form-group">
            <label class="col-form-label"> {{ $t('general.heading') }}</label>
            <input v-model="kvkk_notice.title" class="form-control"
                   :placeholder="$t('general.enter_kvkk_title')" type="text"/>
          </div>

          <div class="form-group">
            <label class="col-form-label"> {{ $t('general.content') }} <span class="required">*</span></label>
            <Summernote 
              :model="kvkk_notice.html" 
              name="kvkk-editor" 
              height="400px"
              @change="kvkk_notice.html = $event"
              :config="summernoteConfig"
            />
          </div>

        </div>
      </div>

      <!-- Privacy Notice Section -->
      <div class="editor-section mb-4">
        <div class="section-header-collapsible" @click="togglePrivacySection">
          <div class="d-flex align-items-center">
            <i class="uil-lock mr-2"></i>
            <div>
              <h4 class="mb-0"> {{ $t('general.privacy_policy') }}</h4>
            </div>
          </div>
          <i :class="['collapse-icon', isPrivacyOpen ? 'uil-angle-up' : 'uil-angle-down']"></i>
        </div>

        <div v-show="isPrivacyOpen" class="section-content">
          <div class="form-group">
            <label class="col-form-label"> {{ $t('general.heading') }}</label>
            <input v-model="privacy_notice.title" class="form-control"
                   :placeholder="$t('general.enter_privacy_title')" type="text"/>
          </div>

          <div class="form-group">
            <label class="col-form-label"> {{ $t('general.content') }} <span class="required">*</span></label>
            <Summernote 
              :model="privacy_notice.html" 
              name="privacy-editor" 
              height="400px"
              @change="privacy_notice.html = $event"
              :config="summernoteConfig"
            />

          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="uk-flex uk-flex-right mt-4">
        <button :disabled="saving" class="btn btn-default" @click="saveLegalTexts">
          <span v-if="saving" class="mr-2" uk-spinner="ratio: 0.6"></span>
          {{ saving ? $t('general.saving') : $t('general.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AppMultiselect from "@/view/components/select/app-multiselect/app-multiselect.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import InformationModal from "@/view/components/modal/informationModal";
import i18nService from "@/core/services/i18n.service.js";

const Summernote = () =>
  import(
    /* webpackChunkName: "summernote-editor" */ "@/view/company/Mail/Summernote.vue"
  );

export default {
  name: "LegalTextsEditor",
  components: {
    Summernote,
    AppMultiselect,
    DefaultLoading,
    InformationModal
  },
  props: {
    companyId: {
      type: Number,
      required: true
    }
  },
  data() {
    const currentLang = i18nService.getActiveLanguage();
    const langObj = i18nService.languages.find(l => l.lang === currentLang);
    
    return {
      loading: false,
      saving: false,
      errorMessages: [],
      successMessage: null,
      selectedLocale: currentLang,
      selectedLanguageObj: langObj || i18nService.languages[0],
      languages: i18nService.languages,
      isKvkkOpen: true,
      isPrivacyOpen: true,
      showPreview: false,
      kvkk_notice: {
        title: "",
        html: ""
      },
      privacy_notice: {
        title: "",
        html: ""
      },
      summernoteConfig: {
        toolbar: [
          ['style', ['style']],
          ['font', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
        ],
        fontNames: ['Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Helvetica', 'Impact', 'Lucida Grande', 'Tahoma', 'Times New Roman', 'Verdana'],
        fontSizes: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '36', '48', '64', '82', '150'],
        placeholder: this.$t('general.write_your_content_here'),
        dialogsInBody: true,
        dialogsFade: true
      }
    };
  },
  computed: {
    currentLocale() {
      return this.selectedLocale;
    }
  },
  methods: {
    async fetchLegalTexts() {
      this.loading = true;
      this.errorMessages = [];

      try {
        const url = `/api/company/${this.companyId}/legal-texts?locale=${this.currentLocale}`;

        const response = await axios.get(url);
        const legalTexts = response.data?.data;

        if (legalTexts) {
          // KVKK Notice
          if (legalTexts[0]) {
            this.kvkk_notice = {
              title: legalTexts[0].title || "",
              html: legalTexts[0].content_html || ""
            };
          } else {
            this.kvkk_notice = { title: "", html: "" };
          }

          // Privacy Notice
          if (legalTexts[1]) {
            this.privacy_notice = {
              title: legalTexts[1].title || "",
              html: legalTexts[1].content_html || ""
            };
          } else {
            this.privacy_notice = { title: "", html: "" };
          }
        }
      } catch (error) {
        this.kvkk_notice = { title: "", html: "" };
        this.privacy_notice = { title: "", html: "" };

        this.errorMessages.push(this.$t("general.legal_texts_load_error"));

      } finally {
        this.loading = false;
      }
    },

    async saveLegalTexts() {
      this.saving = true;
      this.errorMessages = [];
      this.successMessage = null;

      // Validasyon
      if (!this.kvkk_notice.html.trim() && !this.privacy_notice.html.trim()) {
        this.errorMessages.push(this.$t("general.at_least_one_legal_text_required"));
        this.saving = false;
        return;
      }

      try {
        const requests = [];

        if (this.kvkk_notice.html.trim()) {
          const kvkkPayload = {
            key: "kvkk_notice",
            locale: this.currentLocale,
            content_html: this.kvkk_notice.html,
            title: this.kvkk_notice.title,
            is_active: true
          };
          requests.push(
              axios.put(`/api/company/${this.companyId}/legal-texts/upsert`, kvkkPayload)
          );
        }

        if (this.privacy_notice.html.trim()) {
          const privacyPayload = {
            key: "privacy_notice",
            locale: this.currentLocale,
            content_html: this.privacy_notice.html,
            title: this.privacy_notice.title,
            is_active: true
          };
          requests.push(
              axios.put(`/api/company/${this.companyId}/legal-texts/upsert`, privacyPayload)
          );
        }

        await Promise.all(requests);

        this.successMessage = this.$t("general.legal_texts_saved_successfully");

        window.scrollTo({ top: 0, behavior: 'smooth' });

      } catch (error) {
        console.error("Fail:", error);

        const errorMessage = error?.response?.data?.message || this.$t("general.save_failed");
        this.errorMessages.push(errorMessage);

        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        this.saving = false;
      }
    },

    onLanguageChange(selectedLang) {
      // Multiselect'ten gelen language object
      if (selectedLang && selectedLang.lang) {
        this.selectedLocale = selectedLang.lang;
        this.kvkk_notice = { title: "", html: "" };
        this.privacy_notice = { title: "", html: "" };
        this.fetchLegalTexts();
      }
    },

    toggleKvkkSection() {
      this.isKvkkOpen = !this.isKvkkOpen;
    },

    togglePrivacySection() {
      this.isPrivacyOpen = !this.isPrivacyOpen;
    },

    async openPreview() {
      this.showPreview = true;

      await this.$nextTick();

      if (this.$refs.informationModalRef && typeof this.$refs.informationModalRef.fetchLegalTexts === 'function') {
        await this.$refs.informationModalRef.fetchLegalTexts(this.currentLocale);
      }
    },

    onModalClosed() {
      this.showPreview = false;
    },

    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    }
  },

  mounted() {
    this.fetchLegalTexts();
  },

  created() {
    this.debouncedResetValues = _.debounce(this.resetMessages, 3000);
  },

  watch: {
    successMessage(val) {
      if (val) this.debouncedResetValues();
    },
    errorMessages(val) {
      if (val && val.length > 0) this.debouncedResetValues();
    }
  }
};
</script>

<style lang="scss" scoped>
.legal-texts-editor {
  padding: 0;

  .language-selector-section {
    background: #f8f9fa;
    padding: 24px;
    border-radius: 12px;
    border: 1px solid #e3e6ea;
  }

  .selector-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }

  .language-selector-wrapper {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
  }

  .language-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #fff;
    border-radius: 8px;
    border: 2px solid #dee2e6;
    margin-top: 8px;
    
    i {
      font-size: 22px;
      color: #666CFF;
    }
  }

  .language-selector-wrapper :deep(.multiselect) {
    max-width: 250px;
    min-width: 200px;
  }

  .language-selector-wrapper :deep(.multiselect__tags) {
    border: 2px solid #dee2e6;
    border-radius: 8px;
    min-height: 42px;
    padding: 8px 40px 0 12px;
  }

  .language-selector-wrapper :deep(.multiselect__tags:hover),
  .language-selector-wrapper :deep(.multiselect--active .multiselect__tags) {
    border-color: #666CFF;
  }

  .language-selector-wrapper :deep(.multiselect__option--highlight) {
    background: #666CFF;
    color: #fff;
  }

  .preview-btn {
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    border: 2px solid #666CFF;
    color: #666CFF;
    background: #fff;
  }

  .preview-btn:hover {
    background: #666CFF;
    color: #fff;
  }

  .editor-section {
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e9ecef;
  }

  .section-header-collapsible {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 25px;
    cursor: pointer;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
  }

  .section-header-collapsible:hover {
    background: #e9ecef;
  }

  .section-header-collapsible h4 {
    color: #2c3e50;
    font-weight: 600;
    font-size: 1.1rem;
  }

  .section-header-collapsible i {
    font-size: 1.5rem;
    color: #666CFF;
  }

  .collapse-icon {
    font-size: 1.5rem;
    color: #666CFF;
    min-width: 24px;
  }

  .section-content {
    padding: 25px;
  }

  .form-group label {
    font-weight: 500;
    color: #333;
  }

  .required {
    color: #e31748;
    margin-left: 3px;
  }

  /* Summernote Editor */
  :deep(.note-editor) {
    border-radius: 8px;
    border: 1px solid #e3e6ea;
  }

  :deep(.note-editor .note-editable) {
    min-height: 350px;
    font-size: 14px;
    line-height: 1.8;
    padding: 20px;
  }

  /* Toolbar */
  :deep(.note-toolbar) {
    background: #f8f9fa;
    border-bottom: 2px solid #d1d5db;
    padding: 8px 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    min-height: 48px;
  }

  :deep(.note-toolbar .note-btn-group) {
    display: inline-flex;
    align-items: center;
    position: relative;
  }

  :deep(.note-toolbar .note-btn-group:not(:last-child)::after) {
    content: '';
    position: absolute;
    right: -1px;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
    height: 24px;
    background: #d1d5db;
  }

  :deep(.note-toolbar .note-btn) {
    padding: 6px 8px;
    border: none;
    background: transparent;
    color: #323130;
    font-size: 13px;
    border-radius: 2px;
    min-width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 1px;
  }

  :deep(.note-toolbar .note-btn:hover) {
    background: #edebe9;
  }

  :deep(.note-toolbar .note-btn.active) {
    background: #c8c6c4;
  }

  :deep(.note-toolbar .dropdown-menu) {
    border: 1px solid #c8c6c4;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    padding: 4px 0;
    background: #fff;
    min-width: 160px;
  }

  :deep(.note-toolbar .dropdown-item:hover) {
    background: #edebe9;
  }

  :deep(.note-toolbar .dropdown-item.active) {
    background: #c8c6c4;
  }

  :deep(.note-toolbar .note-btn[data-name="codeview"]) {
    margin-left: auto;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .selector-content {
      flex-direction: column;
      gap: 16px;
    }

    .language-selector-wrapper {
      width: 100%;
    }

    .language-selector-wrapper :deep(.multiselect) {
      width: 100%;
      max-width: 100%;
    }

    .preview-btn {
      width: 100%;
      justify-content: center;
    }

    :deep(.note-toolbar) {
      padding: 6px 8px;
      min-height: 44px;
    }

    :deep(.note-toolbar .note-btn) {
      padding: 5px 6px;
      font-size: 12px;
      min-width: 24px;
      height: 24px;
    }
  }
}

</style>

<style>
/* Global styles for Summernote editor lists - Override global ul/ol styles */
.note-editor .note-editable ul {
  list-style-type: disc !important;
  padding-left: 20px !important;
  margin-left: 0 !important;
}

.note-editor .note-editable ul li {
  list-style-type: disc !important;
  display: list-item !important;
}

.note-editor .note-editable ol {
  list-style-type: decimal !important;
  padding-left: 20px !important;
  margin-left: 0 !important;
}

.note-editor .note-editable ol li {
  list-style-type: decimal !important;
  display: list-item !important;
}

/* Summernote Popover */
.note-popover.popover {
  width: auto !important;
  min-width: auto !important;
  max-width: none !important;
}

.note-popover.popover .popover-content {
  width: auto !important;
  padding: 8px;
  white-space: nowrap;
}

</style>
